import { getStoryblokLanguage } from "../../../config/storyblok-config";
import { isStoryblokPreview } from "../../../environment.config.mjs";

const getStartWithLanguageRegExp = () =>
	new RegExp(`^\\/?${getStoryblokLanguage()}`);

// Add locale to url if it is not already present
const addLocale = (url: string) => {
	const storyblokLanguage = getStoryblokLanguage();

	if (
		storyblokLanguage === "default" ||
		getStartWithLanguageRegExp().test(url)
	) {
		return url;
	}

	return `/${storyblokLanguage}${url}`;
};

const removeLocale = (url: string): string => {
	const storyblokLanguage = getStoryblokLanguage();
	if (storyblokLanguage === "default") return url;

	return url.replace(getStartWithLanguageRegExp(), "");
};

const removeLocalPagesPath = (urlWithoutLanguage: string): string => {
	const localPagesPathRegExp = new RegExp(
		`^\\/?local\\/${process.env.GATSBY_STORE}`,
	);

	return urlWithoutLanguage.replace(localPagesPathRegExp, "");
};

export const fixStoryblokPath = (url: string, withLocale?: boolean): string => {
	url = url.startsWith("/") ? url : `/${url}`;
	url = removeLocale(url);
	url = url.toLowerCase();

	if (!isStoryblokPreview()) {
		url = removeLocalPagesPath(url);
	} else if (withLocale !== false) {
		url = addLocale(url); // Add locale to url if it is not already present
	}
	return url;
};

export const fixTranslatedSlugs = (
	pageUrl: string,
	translatedSlugs: {
		lang: string;
		path: string;
		published: boolean;
	}[],
): {
	lang: string;
	path: string;
}[] => {
	let filteredSlugs = translatedSlugs
		.filter(
			(translatedSlug) =>
				translatedSlug.lang.toLowerCase() !== "en-eu" &&
				translatedSlug.lang.toLowerCase() !== "zh" &&
				translatedSlug.lang.toLowerCase() !== "ja-jp" &&
				translatedSlug.published,
		)
		.map((translatedSlug) => {
			let fixedPath = fixStoryblokPath(translatedSlug.path);
			// Ensure the URL ends with a trailing slash, unless it's empty or just "/"
			if (fixedPath && fixedPath !== "/" && !fixedPath.endsWith("/")) {
				fixedPath = `${fixedPath}/`;
			}
			return {
				lang: translatedSlug.lang.toLowerCase(),
				path: fixedPath,
			};
		});

	// Move the current language slug to the beginning of the array
	const currentLanguageSlug = filteredSlugs.find(
		(slug) => slug.lang === process.env.GATSBY_LANGUAGE?.toLowerCase(),
	);
	if (currentLanguageSlug) {
		const otherSlugs = filteredSlugs.filter(
			(slug) => slug !== currentLanguageSlug,
		);
		filteredSlugs = [currentLanguageSlug, ...otherSlugs];
	} else {
		let currentPath = fixStoryblokPath(pageUrl);
		// Ensure the URL ends with a trailing slash, unless it's empty or just "/"
		if (currentPath && currentPath !== "/" && !currentPath.endsWith("/")) {
			currentPath = `${currentPath}/`;
		}

		filteredSlugs = [
			{
				lang: process.env.GATSBY_LANGUAGE?.toLowerCase() ?? "",
				path: currentPath,
			},
			...filteredSlugs,
		];
	}

	// Fix /startpage slugs
	filteredSlugs = filteredSlugs.map((slug) => ({
		...slug,
		path: slug.path.replace("/startpage", ""),
	}));

	return filteredSlugs;
};

export const getUrlFromStoryblokLink = (
	storyblokLink: Queries.LinkFragment | undefined | null,
): string => {
	if (!storyblokLink) return "";
	//Removed check and return of cached_url, reasoning: https://github.com/storyblok/storyblok/issues/174#issuecomment-576684253
	const originalUrl = storyblokLink.url === "" ? undefined : storyblokLink.url;
	const storyFullSlug =
		storyblokLink.story?.full_slug === ""
			? undefined
			: storyblokLink.story?.full_slug;
	if (storyFullSlug === undefined && originalUrl?.startsWith("http")) {
		return originalUrl;
	}
	if (!storyFullSlug && !originalUrl) {
		return "";
	}

	return fixStoryblokPath(storyFullSlug ?? originalUrl ?? "");
};
